import { useCallback } from 'react'

import {
  BufferTrackerReact as BufferTracker,
  Client,
} from '@buffer-mono/tracking-plan'

import { useAccount, useOrganizationId } from '~publish/contexts/AccountContext'

// Module evaluation happens once per page load, so this marks session start.
const LOADED_AT = Date.now()

export type StaleAppReloadTrigger =
  | 'noticeClick'
  | 'updateNavigation'
  | 'sessionAgeNavigation'

/**
 * Tracks the `Stale App Reloaded` event, fired right before the app performs
 * a full page reload onto a fresher bundle. Segment persists its retry queue
 * to localStorage, so the event survives the reload that immediately follows.
 */
export function useStaleAppReloadTracking(): {
  trackStaleAppReloaded: (trigger: StaleAppReloadTrigger) => void
} {
  const { account } = useAccount()
  const organizationId = useOrganizationId()

  const trackStaleAppReloaded = useCallback(
    (trigger: StaleAppReloadTrigger): void => {
      if (!account?.id || !organizationId) return

      BufferTracker.staleAppReloaded({
        accountId: account.id,
        organizationId,
        trigger,
        sessionAgeMs: Date.now() - LOADED_AT,
        clientName: Client.PublishWeb,
      })
    },
    [account?.id, organizationId],
  )

  return { trackStaleAppReloaded }
}
